/**
 * /assistente — o chat em tela cheia, com a lista de conversas ao lado.
 * O dock flutuante (AssistenteDock) usa o mesmo AssistenteChat.
 */

import { useCallback, useEffect, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { toast } from "sonner";
import { MessageSquarePlus, MoreHorizontal, PanelLeftClose, PanelLeftOpen, Pencil, Trash2 } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { toUserFriendlyError } from "@/lib/error-messages";
import { P_ASSISTANT_USE } from "@shared/permissions";
import { AssistenteChat } from "@/features/assistente/AssistenteChat";
import { useAssistente } from "@/features/assistente/useAssistente";

const CHAVE_LISTA = "assistente:lista-aberta";

export default function AssistentePage() {
  return (
    <DashboardLayout>
      <PermissionGuard permission={P_ASSISTANT_USE}>
        <Conteudo />
      </PermissionGuard>
    </DashboardLayout>
  );
}

function Conteudo() {
  const search = useSearch();
  const [, setLocation] = useLocation();
  const idDaUrl = Number(new URLSearchParams(search).get("c"));
  const [conversationId, setConversationId] = useState<number | null>(Number.isInteger(idDaUrl) && idDaUrl > 0 ? idDaUrl : null);
  const utils = trpc.useUtils();
  const conversas = trpc.assistente.conversas.useQuery(undefined, { staleTime: 10_000 });
  const excluir = trpc.assistente.excluir.useMutation({
    onSuccess: () => void utils.assistente.conversas.invalidate(),
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const renomear = trpc.assistente.renomear.useMutation({
    onSuccess: () => void utils.assistente.conversas.invalidate(),
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  // A URL acompanha a conversa aberta, para recarregar e voltar no mesmo lugar.
  useEffect(() => {
    const atual = new URLSearchParams(search).get("c");
    const alvo = conversationId ? String(conversationId) : null;
    if (atual !== alvo) setLocation(alvo ? `/assistente?c=${alvo}` : "/assistente", { replace: true });
  }, [conversationId, search, setLocation]);

  const tituloAtual = conversas.data?.find((c) => c.id === conversationId)?.title;
  const esquecer = useCallback(() => setConversationId(null), []);
  // Lista recolhida: quem está lendo uma resposta longa quer a tela inteira.
  const [listaAberta, setListaAberta] = useState(() => {
    try {
      return localStorage.getItem(CHAVE_LISTA) !== "0";
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(CHAVE_LISTA, listaAberta ? "1" : "0");
    } catch {
      /* armazenamento indisponível: vale só nesta sessão */
    }
  }, [listaAberta]);
  const sessao = useAssistente({ conversationId, onConversaCriada: setConversationId, onConversaInvalida: esquecer });

  return (
    <div className="flex h-[calc(100dvh-3.5rem)] md:h-dvh">
      <aside className={cn("hidden shrink-0 flex-col border-r bg-muted/20 md:flex", listaAberta ? "w-64" : "w-0 overflow-hidden border-r-0")}>
        <div className="flex items-center gap-1 p-3">
          <Button className="flex-1 justify-start" variant="outline" onClick={() => setConversationId(null)}>
            <MessageSquarePlus className="mr-2 h-4 w-4" /> Nova conversa
          </Button>
          <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" title="Recolher conversas" onClick={() => setListaAberta(false)}>
            <PanelLeftClose className="h-4 w-4" />
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
          {(conversas.data ?? []).map((c) => (
            <div
              key={c.id}
              className={cn(
                "group flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm",
                c.id === conversationId ? "bg-accent font-medium" : "hover:bg-accent/60",
              )}
            >
              <button type="button" className="min-w-0 flex-1 truncate text-left" onClick={() => setConversationId(c.id)} title={c.title}>
                {c.title}
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="rounded p-1 opacity-0 hover:bg-background group-hover:opacity-100 data-[state=open]:opacity-100" aria-label="Opções">
                    <MoreHorizontal className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => {
                      const novo = window.prompt("Novo título", c.title);
                      if (novo && novo.trim() && novo.trim() !== c.title) renomear.mutate({ conversationId: c.id, title: novo.trim() });
                    }}
                  >
                    <Pencil className="mr-2 h-3.5 w-3.5" /> Renomear
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={() => {
                      if (!window.confirm("Excluir esta conversa? O histórico some para você.")) return;
                      excluir.mutate({ conversationId: c.id });
                      if (c.id === conversationId) setConversationId(null);
                    }}
                  >
                    <Trash2 className="mr-2 h-3.5 w-3.5" /> Excluir
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
          {conversas.data && conversas.data.length === 0 && <p className="px-2 py-4 text-xs text-muted-foreground">Suas conversas aparecem aqui.</p>}
        </div>
      </aside>
      <section className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-12 shrink-0 items-center gap-2 border-b px-4">
          {!listaAberta && (
            <Button variant="ghost" size="icon" className="hidden h-8 w-8 shrink-0 md:inline-flex" title="Mostrar conversas" onClick={() => setListaAberta(true)}>
              <PanelLeftOpen className="h-4 w-4" />
            </Button>
          )}
          <h1 className="truncate text-sm font-medium">{tituloAtual ?? "Assistente"}</h1>
          <Button size="sm" variant="ghost" className="ml-auto md:hidden" onClick={() => setConversationId(null)}>
            <MessageSquarePlus className="mr-1.5 h-4 w-4" /> Nova
          </Button>
        </header>
        <AssistenteChat sessao={sessao} className="flex-1" />
      </section>
    </div>
  );
}
